import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import { type AdProvider, useAdNetworkStatus } from '../../lib/adNetwork.hooks.js';
import { GradientButton } from '../workflows/alison-run/ui/AlisonDrawerChrome.js';
import { ConnectNetwork } from './ConnectNetwork.js';
import { OnboardingShell } from './OnboardingShell.js';

// The networks whose self-serve OAuth onboarding is live. Meta, Snapchat, Moloco and DV360
// (manual access) come in their own PR.
export const ONBOARDING_PROVIDERS = [
  'linkedin',
  'google',
  'tiktok',
] as const satisfies readonly AdProvider[];
export type OnboardingProvider = (typeof ONBOARDING_PROVIDERS)[number];

export interface OAuthReturn {
  provider: OnboardingProvider;
  connected: boolean;
  error: string | null;
}

export interface NetworksStepProps {
  email: string | null;
  onSignOut: () => void;
  productId: number;
  oauthReturn: OAuthReturn | null;
  onDone: () => void;
}

type Statuses = Record<OnboardingProvider, ReturnType<typeof useAdNetworkStatus>>;

// Nothing is stored here: which networks are in play comes from the same status calls the
// Integrations page reads, so a reload shows what is really connected.
export function useOnboardingStatuses(productId: number | undefined): Statuses {
  return {
    linkedin: useAdNetworkStatus('linkedin', productId),
    google: useAdNetworkStatus('google', productId),
    tiktok: useAdNetworkStatus('tiktok', productId),
  };
}

export function connectedAccounts(statuses: Statuses, provider: OnboardingProvider): number {
  return (statuses[provider].data?.submissions ?? []).filter((s) => !s.error).length;
}

// Every live network as a box in a row; clicking one opens its drawer.
export function NetworksStep({
  email,
  onSignOut,
  productId,
  oauthReturn,
  onDone,
}: NetworksStepProps): ReactNode {
  const statuses = useOnboardingStatuses(productId);
  const anyConnected = ONBOARDING_PROVIDERS.some((p) => connectedAccounts(statuses, p) > 0);

  return (
    <OnboardingShell
      email={email}
      onSignOut={onSignOut}
      step="networks"
      title="Where do your ads hang out?"
      subtitle="Connect the network you advertise on. Alison reads your ads and builds your first insights from them."
      note="One network is enough to start."
      action={
        <GradientButton onClick={onDone} disabled={!anyConnected}>
          Continue
        </GradientButton>
      }
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        {ONBOARDING_PROVIDERS.map((provider) => (
          <ConnectNetwork
            key={provider}
            provider={provider}
            productId={productId}
            status={statuses[provider]}
            accounts={connectedAccounts(statuses, provider)}
            returned={oauthReturn?.provider === provider && oauthReturn.connected}
            returnError={oauthReturn?.provider === provider ? oauthReturn.error : null}
          />
        ))}
      </Box>
    </OnboardingShell>
  );
}
